import React, { useReducer, useEffect } from 'react';

/**
 * `RenewalsJourneyManager` orchestrates the renewals and call-centre journeys.
 *
 * Responsibilities:
 * - Builds initial `JourneyState` from `initialJourneyState` plus router
 *   location state passed from the renewals landing page.
 * - Owns journey state via `useReducer(JourneyReducer, ...)` and exposes a
 *   `JourneyContext` to nested pages via the router `Outlet`.
 * - Filters and updates product documents on route changes.
 * - Registers policies via `PolicyApi.register` and updates navigation status
 *   using `GetNavDispatch`.
 * - Renders shared layout (nav, stepper, page info sidebars, reload guard).
 */
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  DocumentTreeItem,
  PersonalDetailType,
  VehicleState,
  AddressDetails,
  ProductType,
  DirectDebitDetails,
  PolicyServer,
  PolicyResponse,
  NavigationStep,
  JourneyState,
  JourneyContext,
  QuoteProduct,
  RegisterPolicyDto
} from '../../common/CommonTypes';
import { useCurrentPath } from '../useCurrentPath';
import NavBar from '../../common/NavBar';
import {
  functionNotImplemented,
  initialJourneyState,
  Journey,
  PageAbsolutePath,
  PageRelativePath,
  RegistrationState
} from '../../../consts/Common';
import RenewalsNavStepper from './RenewalsNavStepper';
import RenewalsMobileInfoBar from './RenewalsMobileInfoBar';
import {
  filterProductDocuments,
  getSelectedProducts,
  getSelectedVariant
} from '../../common/ProductMethods';
import { PolicyApi } from '../../../api/PolicyApi';
import { getPolicyServer } from '../PolicyMethods';
import RenewalsPageInfo from './RenewalsPageInfo';
import JourneyReducer from '../../../journeyState/JourneyReducer';
import { GetNavDispatch } from '../../common/NavStepper';
import ReloadHandler from '../../common/ReloadHandler';
import { CommonValidation } from '../../common/CommonValidation/commonValidation';
import { useTranslation } from 'react-i18next';
import { getJsonDateString } from '../../common/HelperMethods';
import { useAppSelector } from '../../../app-state/Hooks';
import { checkToken } from '../../common/HelperMethods';

const RenewalsJourneyManager = (): JSX.Element => {
  const currentPath = useCurrentPath();
  const { t } = useTranslation();
  const { state } = useLocation();
  const navigate = useNavigate();

  const user = useAppSelector((s) => s.user);
  const brandWorldPay = useAppSelector((s) => s.brand.worldPay);
  const journeyType = useAppSelector((s) => s.appConfig.journeyType);
  const countryCode = useAppSelector((s) => s.appConfig.countryCode);

  let pageOrder: NavigationStep[];

  const RenewalsPageOrder: NavigationStep[] = [
    {
      page: PageAbsolutePath.Renewals.PersonalDetails,
      pageIndex: 0,
      pageName: t('breadcrumb.personal-details')
    },
    {
      page: PageAbsolutePath.Renewals.VehicleDetails,
      pageIndex: 1,
      pageName: t('breadcrumb.vehicle-details')
    },
    {
      page: PageAbsolutePath.Renewals.Quote,
      pageIndex: 2,
      pageName: t('breadcrumb.quote')
    },
    {
      page: PageAbsolutePath.Renewals.Summary,
      pageIndex: 3,
      pageName: t('breadcrumb.checkout')
    },
    {
      page: PageAbsolutePath.Renewals.SinglePayment,
      pageIndex: 3,
      pageName: t('breadcrumb.checkout')
    },
    {
      page: PageAbsolutePath.Renewals.DirectDebit,
      pageIndex: 3,
      pageName: t('breadcrumb.checkout')
    },
    {
      page: PageAbsolutePath.Renewals.Confirmation,
      pageIndex: 4,
      pageName: t('breadcrumb.confirmation')
    }
  ];
  const CallCentrePageOrder: NavigationStep[] = [
    {
      page: PageAbsolutePath.Renewals.VehicleDetails,
      pageIndex: 0,
      pageName: t('breadcrumb.vehicle-details')
    },
    {
      page: PageAbsolutePath.Renewals.Quote,
      pageIndex: 1,
      pageName: t('breadcrumb.quote')
    },
    {
      page: PageAbsolutePath.Renewals.PersonalDetails,
      pageIndex: 2,
      pageName: t('breadcrumb.customer-details')
    },
    {
      page: PageAbsolutePath.Renewals.Summary,
      pageIndex: 3,
      pageName: t('breadcrumb.checkout')
    },
    {
      page: PageAbsolutePath.Renewals.SinglePayment,
      pageIndex: 3,
      pageName: t('breadcrumb.checkout')
    },
    {
      page: PageAbsolutePath.Renewals.DirectDebit,
      pageIndex: 3,
      pageName: t('breadcrumb.checkout')
    },
    {
      page: PageAbsolutePath.Renewals.Confirmation,
      pageIndex: 4,
      pageName: t('breadcrumb.confirmation')
    }
  ];
  if (journeyType === Journey.CallCentre) {
    pageOrder = CallCentrePageOrder;
  } else {
    pageOrder = RenewalsPageOrder;
  }

  const initialRenewalsJourneyState: JourneyState = initialJourneyState;

  if (state) {
    const {
      personalDetail,
      vehicle,
      dealerCode,
      token,
      tokenExpiresOn,
      policyStartDate,
      isLateRenewal,
      promoCode,
      campaignId
    } = state as JourneyState;

    initialRenewalsJourneyState.personalDetail = personalDetail;
    initialRenewalsJourneyState.vehicle = vehicle;
    initialRenewalsJourneyState.dealerCode = dealerCode;
    initialRenewalsJourneyState.token = token;
    initialRenewalsJourneyState.tokenExpiresOn = tokenExpiresOn;
    initialRenewalsJourneyState.policyStartDate = policyStartDate;
    initialRenewalsJourneyState.isLateRenewal = isLateRenewal;
    initialRenewalsJourneyState.promoCode = promoCode;
    initialRenewalsJourneyState.campaignId = campaignId;
  }

  const [journey, dispatch] = useReducer(
    JourneyReducer,
    initialRenewalsJourneyState
  );

  const {
    navigationStatus,
    registrationFailMsg,
    registrationState,
    products,
    vehicle,
    personalDetail,
    directDebitDetails,
    policyStartDate,
    isLateRenewal,
    documents,
    renewalMarketing
  } = journey;

  useEffect(() => {
    if (CommonValidation.isNotEmpty(registrationFailMsg)) {
      if (
        window.location.pathname === PageAbsolutePath.Renewals.DirectDebit ||
        registrationState === RegistrationState.Failed
      ) {
        navigate(PageRelativePath.RenewalFailedRegistration, {
          relative: 'path'
        });
      }
    }
  }, [registrationFailMsg]);

  useEffect(() => {
    setProductDocuments(products, documents, currentPath);

    checkToken(journey.token, journey.tokenExpiresOn, dispatch);
  }, [currentPath]);

  const setPersonalDetails = (details: PersonalDetailType) => {
    dispatch({
      type: 'set-personal-details',
      personalDetail: details
    });
  };

  const setVehicleDetails = (vehicleState: VehicleState) => {
    dispatch({
      type: 'set-vehicle',
      vehicle: vehicleState
    });
  };

  const onUpdateProductDocuments = (productDocuments: DocumentTreeItem[]) => {
    dispatch({
      type: 'set-documents',
      documents: productDocuments
    });
  };

  const setProductDocuments = (
    initialProducts: ProductType[],
    docs: DocumentTreeItem[],
    path: string
  ): void => {
    const newDocuments = filterProductDocuments(
      initialProducts,
      '',
      docs,
      path
    );

    if (newDocuments) {
      onUpdateProductDocuments(newDocuments);
    }
  };

  const setExpandedDocIds = (docIds: string[]) => {
    dispatch({
      type: 'set-expanded-node-ids',
      expandedDocIds: docIds
    });
  };

  const setProducts = (initialProducts: ProductType[]) => {
    dispatch({
      type: 'set-products',
      products: initialProducts
    });
  };

  const setQuoteProducts = (quoteProducts: QuoteProduct[]) => {
    dispatch({
      type: 'set-quote-products',
      quoteProducts
    });
  };

  const setAddressSinglePayment = (addressDetails: AddressDetails) => {
    dispatch({
      type: 'set-address-single-payment',
      addressDetails: addressDetails
    });
  };

  const setRegistrationFailMsg = (message: string) => {
    dispatch({
      type: 'set-registration-fail-msg',
      message: message
    });
  };

  const setRegistrationState = (regState: number) => {
    dispatch({
      type: 'set-registration-state',
      regState: regState
    });
  };

  const register = (
    cardTransactionId: string,
    bankAuthorisationCode: string,
    campaignId?: number
  ) => {
    const controller = new AbortController();

    const selectedProducts = getSelectedProducts(products);

    const uniquePaymentMethodSelected: string[] = [];

    selectedProducts.forEach((p) => {
      const selectedVariant = getSelectedVariant(p.productVariants);
      const paymentMethod = selectedVariant?.paymentMethod;

      if (
        paymentMethod &&
        !uniquePaymentMethodSelected.find((pm) => pm == paymentMethod)
      ) {
        uniquePaymentMethodSelected.push(paymentMethod);
      }
    });

    const policy: PolicyServer[] = [];

    uniquePaymentMethodSelected.forEach((pm) => {
      const policyDetail = getPolicy(pm, cardTransactionId, campaignId);

      if (policyDetail) {
        policy.push(policyDetail);
      }
    });

    const successCallback = (policyResponse: PolicyResponse[]) => {
      const orderNumbers = policyResponse.map((pr) => pr.policyNumber);

      dispatch({
        type: 'set-order-numbers',
        orderNumbers: orderNumbers,
        authorisationCode: bankAuthorisationCode
      });
    };

    const failureCallback = () => {
      if (cardTransactionId) {
        setRegistrationState(RegistrationState.VoidingPayment);
      } else {
        setRegistrationFailMsg(t('common-error-msg.submission-failed-message'));
      }
    };

    const registerPolicyDto: RegisterPolicyDto = {
      policy: policy,
      allowPastPurchaseDate: true,
      countryCode: countryCode
    };

    setRegistrationState(RegistrationState.Registering);
    new PolicyApi().register(
      registerPolicyDto,
      journey.token,
      successCallback,
      failureCallback,
      controller.signal
    );

    return () => {
      controller.abort();
    };
  };

  const getPolicy = (
    paymentMethod: string,
    cardTransactionId: string,
    campaignId?: number
  ): PolicyServer | undefined => {
    return getPolicyServer(
      paymentMethod,
      cardTransactionId,
      products,
      vehicle,
      personalDetail,
      directDebitDetails,
      getJsonDateString(policyStartDate.toString()),
      isLateRenewal,
      user.dealerCode,
      user.instanceId,
      user.userId || '',
      brandWorldPay,
      undefined,
      renewalMarketing,
      journeyType,
      campaignId,
      user.originalSupplyingDealer
    );
  };

  const onPaymentVoided = () => {
    dispatch({
      type: 'set-registration-fail-msg',
      message: t('common-error-msg.payment-voided-message')
    });
  };

  const onPaymentFailure = () => {
    dispatch({
      type: 'set-registration-fail-msg',
      message: 'Failed to make the payment.'
    });
  };

  const setDirectDebitDetails = (directDebit: DirectDebitDetails) => {
    dispatch({
      type: 'set-direct-debit-details',
      directDebitDetails: directDebit
    });
  };

  const onPageChange = (page: string, status: boolean) => {
    const action = GetNavDispatch(page, status);

    if (action !== undefined) {
      dispatch(action);
    }
  };

  const onPolicyStartDateChange = (policyDate: Date) => {
    dispatch({
      type: 'set-policy-start-date',
      policyStartDate: policyDate
    });
  };

  const onUpdateRenewalMarketing = (consentFlag: boolean) => {
    dispatch({
      type: 'set-consent-to-renewal-marketing',
      renewalMarketing: consentFlag
    });
  };

  const onSetJourney = (journeyState: JourneyState) => {
    dispatch({
      type: 'set-journey-state',
      journeyState
    });
  };

  const onProductDocumentsDownloaded = (downloaded: boolean) => {
    dispatch({
      type: 'set-product-document-downloaded',
      downloaded
    });
  };

  const renewalsJourneyContext: JourneyContext = {
    journey: journey,
    onUpdateVehicleDetails: setVehicleDetails,
    onChangeSalesId: functionNotImplemented,
    onUpdateProducts: setProducts,
    onUpdateQuotedProducts: setQuoteProducts,
    onUpdateProductDocuments: onUpdateProductDocuments,
    onUpdateProductVariant: functionNotImplemented,
    setPersonalDetails: setPersonalDetails,
    setAddressSinglePayment: setAddressSinglePayment,
    setDirectDebitDetails: setDirectDebitDetails,
    onUpdateAgreedTermsAndConditions: functionNotImplemented,
    onRegister: register,
    onPaymentFailure: onPaymentFailure,
    onPaymentVoided: onPaymentVoided,
    onPageChange: onPageChange,
    getPolicy: getPolicy,
    setRegistrationFailMsg: setRegistrationFailMsg,
    setExpandedDocIds: setExpandedDocIds,
    onUpdateFoundExistingQuotes: functionNotImplemented,
    onPolicyStartDateChange: onPolicyStartDateChange,
    onChangeMileage: functionNotImplemented,
    onChangeVehiclePurchasePrice: functionNotImplemented,
    onChangeVehicleDeliveryDate: functionNotImplemented,
    onChangeVehicleGapIntroductionDate: functionNotImplemented,
    onUpdateVehicleVrn: functionNotImplemented,
    onUpdateRenewalMarketing: onUpdateRenewalMarketing,
    onSetJourney: onSetJourney,
    onProductDocumentsDownloaded: onProductDocumentsDownloaded,
    onUpdateManufacturingCoverageMonths: functionNotImplemented
  };

  return (
    <>
      <div className='flex-column main-column'>
        <div className='main-content'>
          <NavBar />
          <RenewalsNavStepper
            products={[]}
            pageOrder={pageOrder}
            navigationStatus={navigationStatus}
            currentPath={currentPath}
          />
          <div className='page-container'>
            <div className='page-header-line' />
            <Outlet context={renewalsJourneyContext} />
            <RenewalsMobileInfoBar
              renewalsJourney={journey}
              currentPath={currentPath}
              setExpandedDocIds={setExpandedDocIds}
            />
          </div>
        </div>
      </div>
      <div className='flex-column desktop-column renewal-pageinfo-section'>
        <RenewalsPageInfo
          renewalsJourney={journey}
          currentPath={currentPath}
          setExpandedDocIds={setExpandedDocIds}
        />
      </div>
      <ReloadHandler reloadPath={PageAbsolutePath.Renewals.Landing} />
    </>
  );
};

export default RenewalsJourneyManager;
